/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { TerminalDetails } from '../../../../shared/entities/terminal';
import { DetailsPanelGroupCollapseState } from '../../../../store/ui/reducer';
import { isOnline } from '../../../../utils/ConnectionStatusUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../../utils/StringUtils';
import CollapseGroup from '../../../common/collapse_group/CollapseGroup';
import CollapseGroupItem from '../../../common/collapse_group/CollapseGroupItem';
import { calculateScrollbarHeightOffset, CollapseGroupScrollbarContainer } from './TerminalTabPanel';
import { renderSpaceVehicleTabHeader } from './shared/SpaceVehicleTabHeader';

interface TerminalTabDataRTEProps extends TerminalTabDataRTEStateProps, TerminalTabDataRTEDispatchProps {}

export interface TerminalTabDataRTEStateProps {
  hidden: boolean;
  satellitesInFOVIndexOffset?: number;
  tailDetailsPanelTerminal?: TerminalDetails;
  detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState;
  isKuPlusKa: boolean;
  isDualTerminal: boolean;
  isOverviewMode: boolean;
  isFullTailViewMode: boolean;
  isNextSatellitePanelEnabled: boolean;
  isSatellitesInFOVEnabled: boolean;
  history: any;
  showDetailedConnectionStats: boolean;
}

export interface TerminalTabDataRTEDispatchProps {
  setDetailsPanelGroupCollapseState: (detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) => {};
}

const FADED_CLASS_NAME = 'tail__details_faded_wrapper';

const TerminalTabDataRTE = ({
  hidden,
  tailDetailsPanelTerminal,
  isFullTailViewMode,
  detailsPanelGroupCollapseState,
  history,
  setDetailsPanelGroupCollapseState,
}: TerminalTabDataRTEProps) => {
  const fadedClassName = isOnline(tailDetailsPanelTerminal?.connectivityStatus) ? '' : FADED_CLASS_NAME;
  const scrollbarHeightOffset = calculateScrollbarHeightOffset(tailDetailsPanelTerminal);

  function toggleGroupCollapsed(groupName: keyof DetailsPanelGroupCollapseState) {
    setDetailsPanelGroupCollapseState({
      ...detailsPanelGroupCollapseState,
      [groupName]: !detailsPanelGroupCollapseState[groupName],
    });
  }

  return !hidden ? (
    <React.Fragment>
      {renderSpaceVehicleTabHeader(tailDetailsPanelTerminal, isFullTailViewMode, history)}
      <CollapseGroupScrollbarContainer scrollbarHeightOffset={scrollbarHeightOffset}>
        {/* Placeholder values until the RTE contact summary is pulled */}
        <CollapseGroup
          data-test="terminal__status__collapse__group__header--contact__summary"
          collapseButtonClicked={() => {
            toggleGroupCollapsed('contactSummaryGroupOpened');
          }}
          open={detailsPanelGroupCollapseState.contactSummaryGroupOpened}
          title="Contact Summary"
        >
          <div className={fadedClassName}>
            <CollapseGroupItem id="contact_last_update" label="Last Update" value={EMPTY_FIELD_STRING_PLACEHOLDER} />
            <CollapseGroupItem id="ground_station" label="Ground Station" value={EMPTY_FIELD_STRING_PLACEHOLDER} />
            <CollapseGroupItem
              id="antenna_tracking_mode"
              label="Antenna Tracking Mode"
              value={EMPTY_FIELD_STRING_PLACEHOLDER}
            />
          </div>
        </CollapseGroup>
        <CollapseGroup
          data-test="terminal__status__collapse__group__header--active__links"
          collapseButtonClicked={() => {
            toggleGroupCollapsed('activeLinksGroupOpened');
          }}
          open={detailsPanelGroupCollapseState.activeLinksGroupOpened}
          title="Active Links"
        >
          <div className={fadedClassName}>
            <CollapseGroupItem id="link_last_update" label="Last Update" value={EMPTY_FIELD_STRING_PLACEHOLDER} />
            <CollapseGroupItem id="link_name" label="Link" value={EMPTY_FIELD_STRING_PLACEHOLDER} />
            <CollapseGroupItem id="contact_type" label="Contact Type" value={EMPTY_FIELD_STRING_PLACEHOLDER} />
            <CollapseGroupItem id="data_volume" label="Data Volume" value={EMPTY_FIELD_STRING_PLACEHOLDER} />
            <CollapseGroupItem id="tracking_signal" label="Tracking Signal" value={EMPTY_FIELD_STRING_PLACEHOLDER} />
          </div>
        </CollapseGroup>
      </CollapseGroupScrollbarContainer>
    </React.Fragment>
  ) : null;
};

export default TerminalTabDataRTE;
